import {FC, useEffect, useLayoutEffect} from 'react';
//formik
import {Field, useFormikContext} from 'formik';
import {CreateEventFormValues} from '../../formConfiguration';
//mui
import {Box, Typography} from '@mui/material';
//constants
import {BODY2, FIELD, FIELD_LABEL, PLACEHOLDER, SUBTITLE1} from '@/utils/constants';
//helpers
import {setRequiredLabel} from '@/utils/helpers';
//components
import Switch from '@/shared/components/Switches/Switch';
import DatePicker from '@/shared/components/DatePicker';
import TimePicker from '@/shared/components/TimePicker';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import Input from '@/shared/components/Inputs/Input';
//styles
import {raffleContentStyle} from './RaffleContent.styles';
import {initialRaffleTires} from '../../TableAndTickets/TicketsTables/helpers';
import Button from '@/shared/components/Button';
import CurrencyField from '@/shared/components/Inputs/CurrencyInput';

const RaffleContent: FC = () => {
  const {
    setFieldValue,
    setFieldTouched,
    values: {isRaffle, raffleTires, isOnlineAuction},
  } = useFormikContext<CreateEventFormValues>();

  useLayoutEffect(() => {
    addTableClickHandler();
  }, []);

  const addTableClickHandler = () => {
    setFieldValue('raffleTires', [...raffleTires, initialRaffleTires]);
  };

  const removeTableClickHandler = (index: number) => {
    if (raffleTires.length === 1) return;

    const newRaffleTires = raffleTires.filter((_, i) => i !== index);
    setFieldValue('raffleTires', newRaffleTires);
  };

  useEffect(() => {
    if (!isRaffle) {
      setFieldTouched('startRaffleDate', false);
      setFieldTouched('endRaffleDate', false);
      setFieldTouched('raffleDescription', false);
    }
  }, [isRaffle]);

  useEffect(() => {
    if (!isOnlineAuction) {
      setFieldTouched('startAuctionDate', false);
      setFieldTouched('endAuctionDate', false);
    }
  }, [isOnlineAuction]);

  return (
    <Box sx={raffleContentStyle.wrapper}>
      <Box sx={raffleContentStyle.raffle}>
        <Typography variant={SUBTITLE1} sx={raffleContentStyle.title}>
          Raffle:
        </Typography>

        <Field id={FIELD.isRaffle} name={FIELD.isRaffle} component={Switch} checked={isRaffle} />

        <Typography variant={BODY2} sx={raffleContentStyle.subtitle}>
          Visible on event webpage
        </Typography>
      </Box>

      <Box sx={raffleContentStyle.dateWrapper}>
        <Box sx={raffleContentStyle.date}>
          <Field
            id={FIELD.startRaffleDate}
            name={FIELD.startRaffleDate}
            label={setRequiredLabel(FIELD_LABEL.startRaffleDate)}
            component={DatePicker}
            sx={raffleContentStyle.dateMonthYear}
            disabled={!isRaffle}
          />
          <Field
            id={FIELD.startRaffleTime}
            name={FIELD.startRaffleTime}
            label={setRequiredLabel(FIELD_LABEL.startTime)}
            component={TimePicker}
            parentClassName={raffleContentStyle.time}
            disabled={!isRaffle}
          />
        </Box>

        <Box sx={raffleContentStyle.date}>
          <Field
            id={FIELD.endRaffleDate}
            name={FIELD.endRaffleDate}
            label={setRequiredLabel(FIELD_LABEL.endRaffleDate)}
            component={DatePicker}
            sx={raffleContentStyle.dateMonthYear}
            disabled={!isRaffle}
          />
          <Field
            id={FIELD.endRaffleTime}
            name={FIELD.endRaffleTime}
            label={setRequiredLabel(FIELD_LABEL.endTime)}
            component={TimePicker}
            parentClassName={raffleContentStyle.time}
            disabled={!isRaffle}
          />
        </Box>
      </Box>

      <Box sx={{marginY: '12px'}}>
        <Field
          id={FIELD.raffleDescription}
          name={FIELD.raffleDescription}
          rows={3}
          sx={raffleContentStyle.raffleDescription}
          placeholder={PLACEHOLDER.raffleDescription}
          label={FIELD_LABEL.raffleDescription}
          component={Input}
          multiline
          disabled={!isRaffle}
        />
      </Box>

      <Box sx={raffleContentStyle.prize}>
        <Box sx={raffleContentStyle.prizeLogoWrapper}>
          <Field
            id={FIELD.prizeLogo}
            name={FIELD.prizeLogo}
            label={FIELD_LABEL.prizeLogo}
            component={GreyUploadImage}
            parentClassName={raffleContentStyle.prizeLogo}
            disabled={!isRaffle}
          />
        </Box>

        <Box sx={raffleContentStyle.raffleDescriptionWrapper}>
          {raffleTires.map((_, index) => (
            <Box key={index} sx={{display: 'flex', gap: '12px', width: '100%'}}>
              <Field
                id={`raffleTires[${index}].numberOfTickets`}
                name={`raffleTires[${index}].numberOfTickets`}
                sx={raffleContentStyle.tickets}
                label={FIELD_LABEL.tickets}
                component={Input}
                isArrayData
                type="text"
                inputProps={{
                  inputMode: 'numeric',
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  },
                }}
                disabled={!isRaffle}
              />
              <CurrencyField
                name={`raffleTires[${index}].price`}
                label={FIELD_LABEL.ticketPrice}
                placeholder={`$ ${PLACEHOLDER.zero}`}
                isArrayData
                disabled={!isRaffle}
              />
            </Box>
          ))}

          <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%'}}>
            <Button
              title="Add new level"
              variant="text"
              size="small"
              onClick={() => addTableClickHandler()}
              sx={{
                marginLeft: 'auto',
              }}
              disabled={!isRaffle || raffleTires.length === 3}
            />

            <Button
              title="Remove last level"
              variant="text"
              size="small"
              onClick={() => removeTableClickHandler(raffleTires.length - 1)}
              sx={{
                marginLeft: 'auto',
              }}
              disabled={!isRaffle || raffleTires.length === 1}
            />
          </Box>

          <Field
            id={FIELD.quantity}
            name={FIELD.quantity}
            placeholder={PLACEHOLDER.unlimited}
            label={FIELD_LABEL.ticketsQuantityRaffle}
            component={Input}
            type="text"
            inputProps={{
              inputMode: 'numeric',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, '');
              },
            }}
            disabled={!isRaffle}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RaffleContent;
